/**
 * The Javascript Date constructor never throws an error, even when provided weird parameters.
 * However, they can be internally invalid.
 */
export function isValidDate(date: Date) {
  return !isNaN(date.getTime())
}

/**
 * Create a valid Date from an ISO 8601 string or returns undefined.
 */
export function dateFromISOString(str: string): Date | undefined {
  const date = new Date(str)
  return isValidDate(date) ? date : undefined
}

/**
 * Returns a human readable string of how long ago the specified date is from now.
 * @param date
 */
export function getDateDiffString(date: Date) {
  const days = getDaysFrom(date)
  const minutes = getMinutesFrom(date)

  if (days > 0)
    return date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    })

  if (minutes > 0) return getRelativeTimeString({ date })

  return "Just now"
}

interface PrettyPrintDateOptions {
  locale?: string
  time?: boolean
}

/**
 * Should mainly be used for debugging or very rare formatting use cases.
 * Example output:  Apr 18, 2025, 03:36 PM
 */
export function prettyPrintDate(
  date: Date,
  options: PrettyPrintDateOptions = {},
) {
  const { locale = "en-US", time = true } = options

  const formatter = new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
    ...(time ? { hour: "2-digit", minute: "2-digit" } : {}),
  })

  return formatter.format(date)
}

/**\
 * Example output:  "2024-12-22"
 */
export function getSimpleISOString(date: Date) {
  const YYYY = date.getUTCFullYear().toString().padStart(4, "0")
  const MM = (date.getUTCMonth() + 1).toString().padStart(2, "0")
  const DD = date.getUTCDate().toString().padStart(2, "0")
  return `${YYYY}-${MM}-${DD}`
}

interface GetRelativeTimeStringArgs {
  date: Date
  lang?: string
  style?: "short" | "long" | "narrow"
}

/**
 * Convert a date to a relative time string, such as
 * "a minute ago", "in 2 hours", "yesterday", "3 months ago", etc.
 * using Intl.RelativeTimeFormat
 *
 * based on Steve Sewells getRelativeTimeString function [Fast and Light Relative Time Strings in JS](https://www.builder.io/blog/relative-time)
 */
export function getRelativeTimeString(args: GetRelativeTimeStringArgs) {
  const { date, lang = "en-US", style = "short" } = args

  // Allow dates or times to be passed
  const timeMs = date.getTime()

  // Get the amount of seconds between the given date and now
  const deltaSeconds = Math.round((timeMs - Date.now()) / 1000)

  // Get numberOfDaysInMonth for the given date to handle months with different number of days
  const numberOfDaysInMonth = getNumberOfDaysInMonth(date)

  // Array reprsenting one minute, hour, day, week, month, etc in seconds
  const cutoffs = [
    60,
    3600,
    86400,
    86400 * 7,
    86400 * numberOfDaysInMonth,
    86400 * 365,
    Infinity,
  ] as const

  // Array equivalent to the above but in the string representation of the units
  const units: Intl.RelativeTimeFormatUnit[] = [
    "second",
    "minute",
    "hour",
    "day",
    "week",
    "month",
    "year",
  ]

  // Grab the ideal cutoff unit
  const unitIndex = cutoffs.findIndex(
    (cutoff) => cutoff > Math.abs(deltaSeconds),
  )

  // Get the divisor to divide from the seconds. E.g. if our unit is "day" our divisor
  // is one day in seconds, so we can divide our seconds by this to get the # of days
  const divisor = unitIndex ? (cutoffs[unitIndex - 1] ?? 1) : 1

  // Intl.RelativeTimeFormat do its magic
  const rtf = new Intl.RelativeTimeFormat(lang, { numeric: "auto", style })
  return rtf.format(
    Math.floor(deltaSeconds / divisor),
    units[unitIndex] ?? "second",
  )
}

export function getNumberOfDaysInMonth(date: Date) {
  return new Date(
    date.getFullYear(),
    date.getMonth() + 1,
    0, // Last day of the month
  ).getDate()
}

export function getMinutesFrom(date: Date) {
  const now = new Date()
  const diff = now.getTime() - date.getTime()

  return Math.floor(diff / (60 * 1000))
}

export function getHoursFrom(date: Date) {
  const minutes = getMinutesFrom(date)

  return Math.floor(minutes / 60)
}

export function getDaysFrom(date: Date) {
  const hours = getHoursFrom(date)

  return Math.floor(hours / 24)
}

export function getMonthsFrom(date: Date) {
  const currentDate = new Date()

  const yearDifference = currentDate.getFullYear() - date.getFullYear()
  const monthDifference = currentDate.getMonth() - date.getMonth()

  return yearDifference * 12 + monthDifference
}

export function addDays(date: Date, numberOfDays: number): Date {
  const newDate = new Date(date)
  newDate.setUTCDate(date.getUTCDate() + numberOfDays)
  return newDate
}
